import { type FC, useState } from "react";
import LoginPopup from "~/features/auth/login/components/LoginPopup";
import RegisterOverlay from "~/features/registration/components/RegisterOverlay";
import { useTranslation } from "react-i18next";
import { Button, Card, Stack, styled, Typography } from "~/common/mui";
import { IMG_LINK_EXPIRED } from "~/utils/url/staticResources";

interface ExpiredPasswordChangePageViewProps {
  onRegisterClick?: () => void;
}

export const ExpiredPasswordChangePageView: FC<ExpiredPasswordChangePageViewProps> = ({ onRegisterClick }) => {
  const { t } = useTranslation();

  return (
    <>
      <StyledCard>
        <IconContainer>
          <ExpiredLinkImage src={IMG_LINK_EXPIRED} alt={t("page.expiredLink.iconAlt")} />
        </IconContainer>
        <Stack gap={3}>
          <Typography variant="headlineMedium">{t("page.expiredLink.title")}</Typography>
          <Stack gap={1}>
            <Typography variant="bodyLarge" color="textSecondary">
              {t("page.expiredLink.subtitle.one")}
            </Typography>
            <Typography variant="bodyLarge" color="textSecondary">
              {t("page.expiredLink.subtitle.two")}
            </Typography>
          </Stack>
          <Button
            onClick={onRegisterClick}
            variant="text"
            data-test-id={"expired-registration-button"}
            sx={{ alignSelf: "center" }}
          >
            {t("page.expiredLink.signup")}
          </Button>
        </Stack>
      </StyledCard>
    </>
  );
};

export const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.surface.bright,
  display: "flex",
  width: "386px",
  padding: "24px",
  flexDirection: "column",
  margin: "0 auto",
  alignItems: "center",
  textAlign: "center",
  gap: "32px",
  marginTop: "115px",
  [theme.breakpoints.down("desktop-s")]: {
    marginTop: "80px",
  },
  [theme.breakpoints.down("tablet")]: {
    marginTop: "72px",
  },
}));

export const IconContainer = styled("div")(({ theme }) => ({
  display: "flex",
  padding: "12px",
  alignItems: "center",
  borderRadius: "50%",
  backgroundColor: theme.palette.primary.container,
}));

export const ExpiredLinkImage = styled("img")({
  height: "40px",
  width: "40px",
});

const ExpiredPasswordChangePage: FC = () => {
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  return (
    <>
      <ExpiredPasswordChangePageView onRegisterClick={() => setIsRegisterOpen(true)} />
      {isRegisterOpen && (
        <RegisterOverlay
          closeRegisterOverlay={() => setIsRegisterOpen(false)}
          onLoginClick={() => {
            setIsRegisterOpen(false);
            setIsLoginOpen(true);
          }}
        />
      )}
      {isLoginOpen && (
        <LoginPopup
          initialEmail=""
          onResetClick={() => undefined}
          close={() => setIsLoginOpen(false)}
          onRegisterClick={() => {
            setIsLoginOpen(false);
            setIsRegisterOpen(true);
          }}
        />
      )}
    </>
  );
};

export function createExpiredPasswordChangePage() {
  return <ExpiredPasswordChangePage />;
}
